import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { getApiUrl, API_ENDPOINTS } from "../config/api";

// Optimized image URL with smaller resolution for faster load

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false); // Loading state
  const [error, setError] = useState(""); // Error state

  const navigate = useNavigate();

  // Handle form submission
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const user = {
      username: username.trim(),
      password: password,
    };

    try {
      // Perform the POST request for login
      const { data } = await axios.post(
        getApiUrl(API_ENDPOINTS.LOGIN),
        user,
        {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );

      // Set tokens in localStorage
      localStorage.setItem("access_token", data.access);
      localStorage.setItem("refresh_token", data.refresh);
      localStorage.setItem("user_id", data.user.id);
      localStorage.setItem("username", data.user.username);

      localStorage.setItem("is_superuser", data.user.is_superuser);
      localStorage.setItem(
        "custom_permissions",
        JSON.stringify(data.user.permissions)
      );
      localStorage.setItem(
        "user_defined_permissions",
        JSON.stringify(data.user.custom_permissions)
      );
      axios.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${data["access"]}`;

      if (data.user.is_superuser) {
        navigate("/analytics");
      } else if (
        data.user.is_staff &&
        data.user.permissions &&
        data.user.permissions.includes("website.custom_user_view") &&
        data.user.custom_permissions &&
        data.user.custom_permissions.length > 0 &&
        !data.user.permissions.includes("auth.Custom_team_leader_perm") &&
        !data.user.permissions.includes("auth.Custom_team_docladder_perm")
      ) {
        // HR users (NOT Team Leaders) created via CreateRecord API
        // Redirect directly to upload records
        navigate("/UserViews", {
          replace: true,
          state: { userPermissions: "hr_with_custom_permissions" },
        });
      } else if (data.user.permissions.includes("auth.CustomUserPermission")) {
        navigate("/OperationDashboard");
      } else if (data.user.permissions.includes("website.update_target")) {
        navigate("/sub_admin_view", {
          replace: true,
          state: { userPermissions: "update_target" },
        });
      } else if (data.user.permissions.includes("website.custom_viewer_perm")) {
        navigate("/viewers", {
          replace: true,
          state: { userPermissions: "custom_viewer_perm" },
        });
      } else if (
        data.user.permissions.includes("website.custom_user_view") &&
        data.user.permissions.includes("auth.center_manager_view")
      ) {
        navigate("/UserViews", {
          replace: true,
          state: {
            userPermissions: "custom_user_view and center_manager_view",
          },
        });
      } else if (
        data.user.permissions.includes("auth.leaderanddocladderteam") &&
        data.user.permissions.includes("auth.Custom_team_leader_perm") &&
        data.user.permissions.includes("website.custom_user_view")
      ) {
        // NEW: Dual role user (Team Leader + HR Associate) - check saved mode
        const savedMode = localStorage.getItem("user_current_mode") || "team_leader";
        if (savedMode === "hr_associate") {
          navigate("/UserViews", {
            replace: true,
            state: { userPermissions: "dual_role_hr_mode" },
          });
        } else {
          navigate("/teamleaderview", {
            replace: true,
            state: { userPermissions: "dual_role_leader_mode" },
          });
        }
      } else if (
        data.user.permissions.includes("auth.Custom_team_leader_perm") &&
        data.user.permissions.includes("auth.leaderanddocladderteam") &&
        data.user.permissions.includes("auth.view_marketing")
      ) {
        navigate("/teamleaderview", {
          replace: true,
          state: {
            userPermissions: "teamleader and docladdermember and marketing",
          },
        });
      } else if (
        data.user.permissions.includes("auth.leaderanddocladderteam")
      ) {
        navigate("/teamleaderview", {
          replace: true,
          state: { userPermissions: "teamleader and docladdermember" },
        });
      } else if (
        data.user.permissions.includes("auth.Custom_team_leader_perm") &&
        data.user.permissions.includes("auth.view_marketing")
      ) {
        navigate("/teamleaderview", {
          replace: true,
          state: { userPermissions: "teamleader and marketing" },
        });
      } else if (
        data.user.permissions.includes("auth.Custom_team_leader_perm")
      ) {
        // MOVED UP: Catch-all for ANY Team Leader - must come BEFORE generic custom_user_view check
        navigate("/teamleaderview", {
          replace: true,
          state: { userPermissions: "Custom_team_leader_perm" },
        });
      } else if (
        data.user.permissions.includes("auth.Custom_team_docladder_perm")
      ) {
        navigate("/teamdocladderview", {
          replace: true,
          state: { userPermissions: "Custom_team_docladder_perm" },
        });
      } else if (data.user.permissions.includes("website.custom_user_view")) {
        // HR users WITHOUT Team Leader permissions go here
        navigate("/UserViews", {
          replace: true,
          state: { userPermissions: "custom_user_view" },
        });
      } else if (data.user.permissions.includes("auth.CustomUserPermission")) {
        navigate("/OperationDashboard", {
          replace: true,
          state: { userPermissions: "custom_operation" },
        });
      } else if (data.user.permissions.includes("auth.hr_permissions")) {
        navigate("/hrview", {
          replace: true,
          state: { userPermissions: "hr_permission" },
        });
      } else if (data.user.permissions.includes("auth.operations")) {
        navigate("/operations", {
          replace: true,
          state: { userPermissions: "operations" },
        });
      } else if (
        data.user.permissions.includes("auth.custom_marketting_view")
      ) {
        navigate("/markettingview", {
          replace: true,
          state: { userPermissions: "custom_marketting" },
        });
      } else if (data.user.permissions.includes("auth.custom_operations")) {
        navigate("/OperationDashboard", {
          replace: true,
          state: { userPermissions: "custom_operations" },
        });
      } else if (
        data.user.custom_permissions &&
        data.user.custom_permissions.length > 0
      ) {
        // Regular users with custom permissions (no employee record, no Django permissions)
        // Redirect to announcements page if they have it, otherwise to the first available feature
        if (data.user.custom_permissions.includes("Announcements")) {
          navigate("/news", {
            replace: true,
            state: { userPermissions: "regular_user_with_custom_permissions" },
          });
        } else if (data.user.custom_permissions.includes("Commitments")) {
          navigate("/CommitmentList", {
            replace: true,
            state: { userPermissions: "regular_user_with_custom_permissions" },
          });
        } else if (
          data.user.custom_permissions.includes("Marketing View") ||
          data.user.custom_permissions.includes("Marketing")
        ) {
          navigate("/marketingFilePage", {
            replace: true,
            state: { userPermissions: "regular_user_with_custom_permissions" },
          });
        } else if (
          data.user.custom_permissions.includes("Minutes of Meeting View") ||
          data.user.custom_permissions.includes("MOM")
        ) {
          navigate("/momlist", {
            replace: true,
            state: { userPermissions: "regular_user_with_custom_permissions" },
          });
        } else if (data.user.custom_permissions.includes("Invoices")) {
          navigate("/invoices", {
            replace: true,
            state: { userPermissions: "regular_user_with_custom_permissions" },
          });
        } else if (data.user.custom_permissions.includes("Company List")) {
          navigate("/CompanyList", {
            replace: true,
            state: { userPermissions: "regular_user_with_custom_permissions" },
          });
        } else if (data.user.custom_permissions.includes("Medical College")) {
          navigate("/MedicalCollegeList", {
            replace: true,
            state: { userPermissions: "regular_user_with_custom_permissions" },
          });
        } else if (data.user.custom_permissions.includes("Deals Page")) {
          navigate("/DealsPage", {
            replace: true,
            state: { userPermissions: "regular_user_with_custom_permissions" },
          });
        } else {
          // If they have custom permissions but no specific route, send to news
          navigate("/news", {
            replace: true,
            state: { userPermissions: "regular_user_with_custom_permissions" },
          });
        }
      } else {
        // No permissions assigned at all
        navigate("/pagenotfound", {
          replace: true,
          state: { userPermissions: "notassigned" },
        });
      }
      console.log("Log In Successful");
    } catch (err) {
      // Set error if login fails
      setError("Invalid username or password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <main className="main-content mt-0">
        <div
          className="page-header align-items-start min-vh-100"
          style={{ backgroundImage: `url('/assets/images/lb2.png')` }}
        >
          <span className="mask bg-gradient-dark opacity-6"></span>
          <div className="container my-auto">
            <div className="row">
              <div className="col-lg-4 col-md-8 col-12 mx-auto">
                <div className="card z-index-0 fadeIn3 fadeInBottom">
                  <div className="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
                    <div
                      className="border-radius-lg py-3 pe-1"
                      style={{ background: "var(--bg-blue)" }}
                    >
                      <h4 className="text-white text-center mb-0">
                        Dashboard Login
                      </h4>
                    </div>
                  </div>
                  <div className="card-body">
                    <form role="form" className="text-start" onSubmit={submit}>
                      <div className="input-group input-group-outline my-3">
                        <label htmlFor="username" className="form-labels w-100">
                          Username
                        </label>
                        <input
                          type="text"
                          value={username}
                          id="username"
                          className="form-control"
                          placeholder="Enter your username"
                          onChange={(e) => setUsername(e.target.value)}
                        />
                      </div>
                      <div className="input-group input-group-outline mb-3">
                        <label htmlFor="password" className="form-labels w-100">
                          Password
                        </label>
                        <input
                          type="password"
                          value={password}
                          id="password"
                          className="form-control"
                          placeholder="Enter your password"
                          onChange={(e) => setPassword(e.target.value)}
                        />
                      </div>

                      {/* Display error message if login fails */}
                      {error && (
                        <div className="alert alert-danger">{error}</div>
                      )}

                      {/* Disable button and show loading while login request is in progress */}
                      <div className="text-center">
                        <button
                          type="submit"
                          className="btn w-100 my-4 mb-2"
                          style={{
                            background: "var(--bg-blue)",
                            color: "white",
                          }}
                          disabled={loading}
                        >
                          {loading ? "Signing in..." : "Sign in"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default Login;
